/**
 * Texture documents read by id, ONCE per page session.
 *
 * A texture document does not change while a design is being edited — it is
 * the catalog's description of a finish — but the viewer used to read it
 * from Firestore every time a surface was re-tiled: every wall, floor and
 * ceiling on every finish pick, 285–430 ms a read and the same floor twice
 * (measured 2026-10-20, Garrett call item #3). One read per id now serves
 * them all, and two asked at once share it.
 *
 * Only a found record is kept. A miss (not found, or a read that failed) is
 * asked again next time. A texture changed or deleted through the texture
 * services is forgotten so the next read sees it (`forgetTextureDoc`); one
 * edited anywhere else shows on the next page load, as before.
 *
 * Kept free of Firebase so it can be tested without an environment.
 */

const cache = new Map();

/**
 * Callers get their own copy (top level and `properties`), so one caller
 * adjusting what it was handed cannot change what the next one reads.
 */
const copyOf = (record) => {
  if (!record) return record;
  const copy = { ...record };
  if (record.properties && typeof record.properties === 'object') {
    copy.properties = { ...record.properties };
  }
  return copy;
};

/**
 * @param {string} scope - which reader's answer this is (the two texture
 *   services search different collections, so their answers differ)
 * @param {string} id
 * @param {() => Promise<Object|null>} read - the uncached read
 * @returns {Promise<Object|null>}
 */
export function readTextureDocOnce(scope, id, read) {
  const key = `${scope}:${id}`;
  let pending = cache.get(key);
  if (!pending) {
    pending = Promise.resolve()
      .then(read)
      .then(
        (record) => {
          if (!record) cache.delete(key);
          return record;
        },
        (error) => {
          cache.delete(key);
          throw error;
        }
      );
    cache.set(key, pending);
  }
  return pending.then(copyOf);
}

/** Forget one texture in every scope — after it was changed or deleted. */
export function forgetTextureDoc(id) {
  for (const key of [...cache.keys()]) {
    if (key.endsWith(`:${id}`)) cache.delete(key);
  }
}

/** For tests. */
export function clearTextureDocCache() {
  cache.clear();
}
